import { useEffect, useState } from "react";

// A *live* matchMedia subscription, not a one-time window.innerWidth read:
// the answer has to stay correct when the window is resized or a tablet is
// rotated, and it has to be resolved by the same engine that resolves the CSS
// (a JS breakpoint compared against innerWidth disagrees with the CSS one by
// the scrollbar width, which is exactly the case where a `display:none`
// element has no visible target).
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

export const DESKTOP_QUERY = "(min-width: 1024px)";

/** Matches Tailwind's `lg` breakpoint -- the one the app switches nav surfaces
 *  (sidebar vs. bottom tab bar) at. */
export function useIsDesktop() {
  return useMediaQuery(DESKTOP_QUERY);
}

/** True for mouse/trackpad-class pointers. Drag-and-drop rides on HTML5 DnD,
 *  which touch browsers don't fire at all -- and a drag gesture on a touch
 *  calendar is a scroll, so an accidental reschedule is the failure mode. */
export function useHasFinePointer() {
  return useMediaQuery("(hover: hover) and (pointer: fine)");
}
